import type { MetaFunction } from '@remix-run/node'
import { Link } from '@remix-run/react'
import { ArrowRight, Check, X } from 'lucide-react'
import { buttonVariants } from '#app/components/ui/button'
import { cn } from '#app/utils/misc'
import { siteConfig } from '#app/utils/constants/brand'
import { ROUTE_PATH as LOGIN_PATH } from '#app/routes/auth+/login'

export const ROUTE_PATH = '/cloudmailin-alternative' as const

const TITLE = 'CloudMailin Alternative — Email to Webhook | Inbox Bridge'
const DESCRIPTION =
  'Looking for a CloudMailin alternative? Inbox Bridge parses inbound email into JSON webhooks with retries, replay, and transparent pricing from $0/month.'
const URL = `${siteConfig.siteUrl}/cloudmailin-alternative`

const BREADCRUMB_JSON_LD = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: siteConfig.siteUrl },
    { '@type': 'ListItem', position: 2, name: 'CloudMailin Alternative', item: URL },
  ],
}

export const meta: MetaFunction = () => {
  return [
    { title: TITLE },
    { name: 'description', content: DESCRIPTION },
    { tagName: 'link', rel: 'canonical', href: URL },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: URL },
    { property: 'og:title', content: TITLE },
    { property: 'og:description', content: DESCRIPTION },
    { property: 'og:image', content: siteConfig.siteImageUrl },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: TITLE },
    { name: 'twitter:description', content: DESCRIPTION },
    { 'script:ld+json': BREADCRUMB_JSON_LD },
  ]
}

type Row = { feature: string; bridge: boolean | string; competitor: boolean | string }

const COMPARISON: Row[] = [
  { feature: 'Inbound email → JSON webhook', bridge: true, competitor: true },
  { feature: 'Free tier', bridge: '1,000 emails/mo', competitor: 'Free volume tier' },
  { feature: 'Automatic retries with backoff', bridge: true, competitor: true },
  { feature: 'One-click delivery replay', bridge: true, competitor: false },
  { feature: 'Per-bridge delivery logs', bridge: true, competitor: true },
  { feature: 'Hash-based private addresses', bridge: true, competitor: false },
  {
    feature: 'Attachment storage & download links',
    bridge: 'Built in, zero config',
    competitor: 'Bring your own S3/Azure/GCS bucket',
  },
  { feature: 'Starting price', bridge: '$14/mo', competitor: 'See cloudmailin.com' },
]

function Cell({ value }: { value: boolean | string }) {
  if (typeof value === 'string') {
    return <span className="text-sm text-muted-foreground">{value}</span>
  }
  return value ? (
    <Check className="h-5 w-5 text-primary" />
  ) : (
    <X className="h-5 w-5 text-muted-foreground/50" />
  )
}

export default function CloudMailinAlternative() {
  return (
    <main className="mx-auto flex w-full max-w-screen-md flex-col gap-8 px-6 py-20">
      <header className="flex flex-col gap-4">
        <h1 className="text-4xl font-bold tracking-tight text-primary md:text-5xl">
          The simple CloudMailin alternative
        </h1>
        <p className="text-lg leading-relaxed text-muted-foreground">
          Inbox Bridge does one thing: turn inbound email into JSON webhooks. Modern
          dashboard, predictable pricing, and a generous free tier.
        </p>
      </header>

      <section className="flex flex-col gap-4">
        <h2 className="text-2xl font-semibold text-primary">Feature comparison</h2>
        <div className="overflow-hidden rounded-lg border border-border">
          <table className="w-full text-left">
            <thead className="bg-secondary/50">
              <tr>
                <th className="p-3 text-sm font-medium text-primary">Feature</th>
                <th className="p-3 text-sm font-medium text-primary">Inbox Bridge</th>
                <th className="p-3 text-sm font-medium text-primary">CloudMailin</th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON.map((row) => (
                <tr key={row.feature} className="border-t border-border">
                  <td className="p-3 text-sm text-muted-foreground">{row.feature}</td>
                  <td className="p-3">
                    <Cell value={row.bridge} />
                  </td>
                  <td className="p-3">
                    <Cell value={row.competitor} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-muted-foreground">
          Comparison based on publicly listed features at the time of writing. CloudMailin
          offers a free tier for inbound email volume. Pricing can change, so check
          CloudMailin&apos;s site for the latest details.
        </p>
      </section>

      <section className="flex flex-col gap-4">
        <h2 className="text-2xl font-semibold text-primary">
          Why teams switch to Inbox Bridge
        </h2>
        <ul className="flex list-disc flex-col gap-2 pl-6 leading-relaxed text-muted-foreground">
          <li>Free tier means you can prototype without a credit card.</li>
          <li>Delivery replay lets you recover from downstream outages instantly.</li>
          <li>Clean JSON payloads modeled for modern APIs, not raw SMTP dumps.</li>
          <li>Hash-based addresses keep endpoints unguessable.</li>
          <li>
            Attachments are stored for you and arrive as secure, expiring download
            links — no bucket to configure or pay for.
          </li>
        </ul>
      </section>

      <div className="flex gap-3">
        <Link
          to={LOGIN_PATH}
          className={cn(buttonVariants({ size: 'lg' }), 'gap-2 font-semibold')}>
          Try Inbox Bridge free
          <ArrowRight className="h-4 w-4" />
        </Link>
        <Link
          to="/email-to-webhook"
          className={cn(buttonVariants({ variant: 'outline', size: 'lg' }))}>
          See how it works
        </Link>
      </div>

      <section className="mt-8 border-t border-border/40 pt-8">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-primary">
          Related
        </h2>
        <ul className="flex flex-col gap-2 text-base text-muted-foreground">
          <li>
            <Link
              to="/email-to-webhook"
              prefetch="intent"
              className="font-medium text-primary underline underline-offset-4 hover:no-underline">
              Email to webhook explained
            </Link>
            {' — '}what an email-to-webhook service does and why teams use one.
          </li>
          <li>
            <Link
              to="/guides/inbound-email-webhook"
              prefetch="intent"
              className="font-medium text-primary underline underline-offset-4 hover:no-underline">
              Inbound email webhook tutorial
            </Link>
            {' — '}five-minute setup guide with sample payloads.
          </li>
        </ul>
      </section>
    </main>
  )
}
